import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import GuardianDeclaration from './GuardianDeclaration';

interface GuardianDeclarationDialogProps {
  /** The child, as they should be named to the person answering. */
  name: string;
  /** Whether this adds entries to an account they already hold. */
  existing: boolean;
  saving: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

/**
 * The consent, in the guardian's own words: I hold parental responsibility, I
 * consent to an account being opened on this child's behalf, I have read the
 * policy section on managed accounts.
 *
 * This is the art. 8 GDPR consent and the one thing the whole flow exists to
 * collect, so it is a deliberate act with nothing else on the screen — and
 * nothing is sent until the box is ticked. Shared by the page the letter
 * opens and the request card on the dashboard: the same words either way,
 * because the server records the same thing.
 */
export default function GuardianDeclarationDialog({
  name,
  existing,
  saving,
  onOpenChange,
  onConfirm,
}: GuardianDeclarationDialogProps) {
  const { t } = useTranslation();
  const [declared, setDeclared] = useState(false);

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {existing
              ? t('guardian.accept.confirmExistingTitle', { name })
              : t('guardian.accept.confirmTitle', { name })}
          </DialogTitle>
          <DialogDescription>{t('guardian.accept.confirmDescription', { name })}</DialogDescription>
        </DialogHeader>

        <GuardianDeclaration name={name} checked={declared} onCheckedChange={setDeclared} />

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={!declared || saving}
            data-testid="confirm-open-ward"
          >
            {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
            {t('guardian.accept.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
